import { Box, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { IEntityBase } from 'app/models/responses/IEntityBase';
import { SortOrder } from 'app/types/data';
import { useDeepCompareMemoize } from 'hooks/useDeepCompareMemoize';
import type { OrderBy } from 'odata-query';
import React, { useMemo, useState } from 'react';

type OrderByCountType<T> = {
  [P in keyof T]: `${P & string}/$count`;
}[keyof T];

interface FieldOption<T extends IEntityBase> {
  field: keyof T | OrderByCountType<T>;
  label: string;
}

type OrderByValue<T extends IEntityBase> = `${string} ${SortOrder}` & OrderBy<T>;

interface Props<T extends IEntityBase> {
  fields: FieldOption<T>[];
  onOrder: (orderBy: OrderBy<T>) => void;
  size?: 'small' | 'medium';
  defaultOrderBy?: OrderBy<T>;
}

interface OrderByOption<T extends IEntityBase> {
  value: OrderByValue<T>;
  label: React.ReactNode;
}

const buildOrderByOptions = <T extends IEntityBase>(fields: FieldOption<T>[]) => {
  let res: OrderByOption<T>[] = [];
  for (const field of fields) {
    const fieldValue = field.field as string;
    res.push({
      value: `${fieldValue} asc`,
      label: <>{field.label} Ascending</>,
    });
    res.push({
      value: `${fieldValue} desc`,
      label: <>{field.label} Descending</>,
    });
  }
  return res;
};

const ODataOrderBy = <T extends IEntityBase>({
  fields,
  onOrder,
  size,
  defaultOrderBy,
}: Props<T>) => {
  // to allow inline array prop declaration without unneccessary component rerendering
  const fieldsMemo = useDeepCompareMemoize(fields);
  const orderByOptions = useMemo(() => buildOrderByOptions(fieldsMemo || []), [fieldsMemo]);
  const [value, setValue] = useState(
    (defaultOrderBy as OrderByValue<T>) || orderByOptions[0].value,
  );

  const handleChange = (event: React.ChangeEvent<any>) => {
    const val = event.target.value as OrderByValue<T>;
    setValue(val);
    onOrder(val);
  };

  return (
    <Box>
      <FormControl fullWidth variant='outlined' size={size}>
        <InputLabel>Order By</InputLabel>
        <Select label='Order By' value={value} onChange={handleChange} variant='outlined'>
          {orderByOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ODataOrderBy;
